/// <amd-module name="Build/Pipelines/Components/StatusBadgePanel" />
import * as React from "react";

import * as Resources from "Build/Pipelines/Resources";
import { StatusBadgePanelStore, IBadgeOption } from "Build/Pipelines/Stores/StatusBadgePanelStore";
import { CopyToClipboardButton } from "Build/Pipelines/Components/CopyToClipboardButton";

import { BranchDropdown } from "Build/Common/Library/Legacy/Components/BranchDropdown";

import { RepositoryTypes } from "Build/SourceProviders/Common";
import { ISourceProviderService } from 'Build/SourceProviders/SourceProvider';

import { ContentSize } from "VSSUI/Callout";
import { Dropdown } from "VSSUI/Dropdown";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { PickListDropdown, IPickListItem } from "VSSUI/PickList";
import { TextField } from "VSSUI/TextField";
import { getSafeId } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { RepositoryResourceData } from "Build/Client/Contracts/DataProvider";
import { VssComponent } from "VSS/Platform/Layout";


export interface IStatusBadgePanelProps {
    statusBadgePanelStore: StatusBadgePanelStore;
}

export interface IStatusBadgePanelState {
    statusBadgeUrl: string;
    sampleMarkdown: string;
    isOpen: boolean;
}

export class StatusBadgePanel extends VssComponent<IStatusBadgePanelProps, IStatusBadgePanelState> {
    private _store: StatusBadgePanelStore;
    private _errorMessage: IObservableValue<string> = new ObservableValue("");
    private _repoDropdownSelection = new DropdownSelection();

    constructor(props: IStatusBadgePanelProps) {
        super(props);

        this._store = props.statusBadgePanelStore;
        this._repoDropdownSelection.select(0);

        this.state = {
            statusBadgeUrl: "",
            sampleMarkdown: "",
            isOpen: false,
        };
    }

    public componentWillMount() {
        this._store.addListener(this._onStoreChanged);
    }

    public componentWillUnmount() {
        this._store.removeListener(this._onStoreChanged);
    }

    public render() {
        if (this.state.isOpen) {
            return (
                <Panel
                    titleProps={{ text: Resources.StatusBadgePanelTitle }}
                    size={ContentSize.Large}
                    onDismiss={this._onDismissed}>
                    <div className="flex-grow">
                        <Observer errorMessage={this._errorMessage}>
                            {(props: { errorMessage: string }) =>
                                props.errorMessage !== "" &&
                                <MessageCard className="margin-bottom-16" severity={MessageCardSeverity.Error}>
                                    {props.errorMessage}
                                </MessageCard>
                            }
                        </Observer>
                        <img
                            className="status-badge-image"
                            src={this.state.statusBadgeUrl}
                            alt={Resources.StatusBadgeImageAltText} />
                        <FocusZone
                            direction={FocusZoneDirection.Vertical}
                            handleTabKey={true}
                            circularNavigation={true}>
                            {this._store.repositories && this._store.repositories.length > 1 && 
                                <div className="margin-top-16">
                                    {this._getRepositorySelector()}
                                </div>
                            }
                            <div className="margin-top-16">
                                <Observer repository={this._store.repositorySelection}>
                                    {(observerProps: {repository: RepositoryResourceData}) => 
                                        this._getBranchSelector(observerProps.repository)
                                    }
                                </Observer>
                            </div>
                            <div className="margin-top-16">
                                {this._getScopeSelector()}
                            </div>
                            <div className="status-badge-text-wrapper">
                                <TextField
                                    className="status-badge-url-textfield"
                                    label={Resources.StatusBadgeUrlLabel}
                                    value={this.state.statusBadgeUrl}
                                    readOnly={true} />
                                <CopyToClipboardButton
                                    className="status-badge-url-copy-button"
                                    textToCopy={() => this.state.statusBadgeUrl} />
                            </div>
                            <div className="status-badge-text-wrapper">
                                <TextField
                                    className="status-badge-url-textfield"
                                    label={Resources.StatusBadgeMarkdownLabel}
                                    value={this.state.sampleMarkdown}
                                    readOnly={true} />
                                <CopyToClipboardButton
                                    className="status-badge-url-copy-button"
                                    textToCopy={() => this.state.sampleMarkdown} />
                            </div>
                        </FocusZone>
                    </div>
                </Panel>
            );
        }
        else {
            return null;
        }
    }
    private _getRepositorySelector() {
        const repositoryListItems: IListBoxItem<RepositoryResourceData>[] = this._store.repositories!.map(repository => {
            const sourceProviderService = this.context.pageContext.getService<ISourceProviderService>("ISourceProviderService");
            const sourceProvider = sourceProviderService.getSourceProvider(repository.type);
            const iconName = sourceProvider ? sourceProvider.getRepoIconName() : "";
            
            return {
                id: repository.id || repository.name,
                text: repository.name,
                iconProps: {
                    iconName: iconName
                },
                data: repository
            }
        });
        return (
            <div>
                <label htmlFor={getSafeId("statusbadge-repository")} className="status-badge-label">{Resources.RepositoryDropdownLabel}</label>
                <div id={getSafeId("statusbadge-repository")}>
                    <Dropdown
                        items={new ArrayItemProvider(repositoryListItems)}
                        onSelect={(event, item) => item.data && this._store.onRepositoryChange(item.data)}
                        selection={this._repoDropdownSelection} />
                </div>
            </div>
        );
    }

    private _getBranchSelector(repository: RepositoryResourceData) {
        const connectionId = repository ? repository.connectionId : "";
        const currentBranch = this._store.branchSelection;
        const isTfvc = repository && repository.type && repository.type.toLowerCase() === RepositoryTypes.TfsVersionControl.toLowerCase();

        if (repository) {
            return (
                <div>
                    <div id={getSafeId("statusbadge-branch")} className="status-badge-branch-selector">
                        <BranchDropdown
                            key={repository.id || repository.name}
                            className={"vertical-spacing flex-column label"}
                            label={Resources.StatusBadgeBranch}
                            repositoryType={repository.type}
                            repositoryId={repository.id || repository.name}
                            defaultBranch={currentBranch}
                            connectionId={connectionId}
                            sourceVersion={isTfvc ? currentBranch : undefined}
                            onVersionSelected={isTfvc ? this._store.onBranchFilterChange : undefined}
                            onBranchSelected={this._store.onBranchFilterChange}
                            onError={this._onError}
                            isDrodownFullWidth={true}
                        />
                    </div>
                </div>
            );
        }
    }

    private _getScopeSelector() {
        return (
            <div>
                <label htmlFor={getSafeId("statusbadge-scope")} className="status-badge-label">{Resources.StatusBadgeScope}</label>
                <div id={getSafeId("statusbadge-scope")}>
                    <PickListDropdown
                        getListItem={this._getListItem}
                        getPickListItems={this._getPickListItems}
                        selectedItems={[this._store.scopeSelection]}
                        onSelectionChanged={this._store.onScopeChange} />
                </div>
            </div>
        );
    }

    private _getListItem = (item: IBadgeOption): IPickListItem => {
        return { key: item.id, name: item.displayName }
    }

    private _getPickListItems = (): IBadgeOption[] => {
        return this._store.scopeOptions
    }

    private _onDismissed = () => {
        this._store.hidePanel();
    }

    private _onStoreChanged = () => {
        if (this._store.badgeUrls) {
            this.setState({
                statusBadgeUrl: this._store.badgeUrls.statusBadgeUrl,
                sampleMarkdown: this._store.badgeUrls.sampleMarkdown,
                isOpen: true
            })
        }
        else {
            this.setState({
                statusBadgeUrl: "",
                sampleMarkdown: "",
                isOpen: false
            });
        }
    }

    private _onError = (errorMessage: string) => {
        this._errorMessage.value = errorMessage;
    }
}
